import {
  fetchRenderManifestWithMeta,
  isKryspaceRenderServiceError,
  type FetchRenderManifestResult,
  type AttentionManifestQuery,
} from '../services/kryspaceRenderService';
import {
  fetchControlBridgeStructureWithMeta,
  type FetchControlBridgeStructureResult,
} from '../services/kryspaceControlBridgeStructureService';
import { getApiErrorMessage } from './apiErrors';

const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 400;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** True for transient server failures during CB manifest/structure load. */
export function shouldRetryControlBridgeLoad(err: unknown): boolean {
  if (isKryspaceRenderServiceError(err)) {
    if (err.code === 'SERVICE_UNAVAILABLE') return true;
    if (
      err.code === 'NETWORK' &&
      (err.httpStatus === 500 ||
        err.httpStatus === 502 ||
        err.httpStatus === 503 ||
        err.httpStatus === 504 ||
        err.httpStatus === undefined)
    ) {
      return true;
    }
    return false;
  }
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 500 || status === 502 || status === 503 || status === 504;
}

export interface ControlBridgeLoadResult {
  manifestResult: FetchRenderManifestResult;
  structureResult: FetchControlBridgeStructureResult;
}

/**
 * Parallel manifest + structure with short backoff on 500/503.
 * Backend CB-purpose load uses singleflight+TTL so both share one Postgres rebuild.
 */
export async function loadControlBridgeWorkspace(
  slug: string,
  query?: AttentionManifestQuery
): Promise<ControlBridgeLoadResult> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    try {
      const [manifestResult, structureResult] = await Promise.all([
        fetchRenderManifestWithMeta(slug, query),
        fetchControlBridgeStructureWithMeta(slug),
      ]);
      return { manifestResult, structureResult };
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_ATTEMPTS - 1 && shouldRetryControlBridgeLoad(err)) {
        await sleep(RETRY_BASE_MS * (attempt + 1));
        continue;
      }
      throw err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Failed to load Control Bridge');
}

export function controlBridgeLoadErrorMessage(err: unknown, code?: string): string {
  const normalizedCode = (code ?? '').toUpperCase();
  if (normalizedCode === 'NOT_CONFIGURED' || normalizedCode === 'UNKNOWN_SLUG') {
    return getApiErrorMessage(err, 'Workspace not configured.');
  }
  if (normalizedCode === 'NOT_FOUND' || normalizedCode === 'FORBIDDEN') {
    if (isKryspaceRenderServiceError(err) && err.message) {
      return err.message;
    }
    return getApiErrorMessage(err, 'Not allowed to read this workspace.');
  }

  const fromApi = getApiErrorMessage(err, '');
  if (fromApi && !fromApi.includes('status code 500')) {
    return fromApi;
  }
  if (
    isKryspaceRenderServiceError(err) &&
    err.message &&
    !err.message.includes('status code 500')
  ) {
    return err.message;
  }
  return 'Control Bridge could not build the workspace view. This is usually temporary — use Retry.';
}
